/**
 * "Upgrade to a Gift Set" button. Shared between the Cart Drawer and the
 * full Cart Page (Mobile) -- the same markup/snippet renders in both
 * places, and this script detects which one a given click happened in
 * (via `button.closest('cart-drawer')`) rather than relying on two
 * separate scripts, so there is exactly one add+remove implementation to
 * keep correct.
 *
 * Reuses the theme's existing primitives instead of building a parallel
 * refresh mechanism:
 * - `fetchConfig()` (assets/global.js) for request headers, same as
 *   assets/soluscent-shipping-protection.js's own programmatic cart calls.
 * - Drawer mode: `cart-drawer` element's own `getSectionsToRender()` /
 *   `renderContents()` (assets/cart-drawer.js) -- the exact function
 *   assets/product-form.js calls after a normal Add to Cart.
 * - Page mode: the same `main-cart-items` / `main-cart-footer` /
 *   `cart-icon-bubble` / `cart-live-region-text` section ids
 *   assets/cart.js's own CartItems.getSectionsToRender() already uses for
 *   quantity changes on the full Cart Page, patched the same way
 *   assets/soluscent-shipping-protection.js's patchSections() already
 *   does (whole-section swap, not just .js-contents, so the Delivery
 *   Protection toggle and free-shipping bar -- which both live outside
 *   .js-contents in main-cart-footer.liquid -- update correctly too).
 * - `PUB_SUB_EVENTS.cartUpdate` so Delivery Protection's reconcile()
 *   subscriber (assets/soluscent-shipping-protection.js) still runs.
 *   Drawer mode reuses source: 'product-form', matching the existing
 *   skip-condition in assets/cart.js for CART-DRAWER-ITEMS. Page mode
 *   uses a distinct source ('soluscent-gift-upgrade-page') paired with a
 *   matching skip-condition added to assets/cart.js's CartItems
 *   subscriber, so its own onCartUpdate() doesn't re-fetch main-cart-items
 *   a second time on top of the patch this script already did.
 *
 * Loaded via a <script> tag that snippets/soluscent-cart-gift-upgrade.
 * liquid now renders UNCONDITIONALLY (not gated behind the module's own
 * eligibility), specifically so the click delegation below reliably
 * binds during a real, browser-parsed page/drawer load -- a <script>
 * tag injected later via element.innerHTML = ... (every dynamic cart
 * re-render path uses that) never executes, so gating this tag behind
 * eligibility used to mean a cart that only becomes eligible *after*
 * the real page load could render a fully-eligible, fully-styled button
 * with no click handler ever attached to the page at all. The guard
 * just below additionally makes this init idempotent in case the tag
 * ever ends up present more than once in the same document (e.g. the
 * Cart Page's own header includes the Drawer markup too, and both
 * happen to be eligible on the same initial load).
 */
(function () {
  if (window.__soluscentGiftUpgradeBound) return;
  window.__soluscentGiftUpgradeBound = true;

  function getMode(button) {
    return button.closest('cart-drawer') ? 'drawer' : 'page';
  }

  function setBusy(button, busy) {
    button.disabled = busy;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
    var spinner = button.querySelector('.soluscent-cart-gift-upgrade__spinner');
    if (spinner) spinner.classList.toggle('hidden', !busy);
  }

  function getSectionsToRequest(mode) {
    if (mode === 'drawer') {
      var cartDrawer = document.querySelector('cart-drawer');
      return cartDrawer ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; }) : [];
    }
    var ids = [];
    var itemsEl = document.getElementById('main-cart-items');
    var footerEl = document.getElementById('main-cart-footer');
    if (itemsEl && itemsEl.dataset.id) ids.push(itemsEl.dataset.id);
    if (footerEl && footerEl.dataset.id) ids.push(footerEl.dataset.id);
    ids.push('cart-icon-bubble');
    ids.push('cart-live-region-text');
    return ids;
  }

  function applySections(mode, state) {
    if (mode === 'drawer') {
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) cartDrawer.renderContents(state);
      return;
    }

    if (!state.sections) return;

    var itemsEl = document.getElementById('main-cart-items');
    if (itemsEl && itemsEl.dataset.id && state.sections[itemsEl.dataset.id]) {
      var itemsDoc = new DOMParser().parseFromString(state.sections[itemsEl.dataset.id], 'text/html');
      var itemsSource = itemsDoc.querySelector('.js-contents');
      var itemsTarget = itemsEl.querySelector('.js-contents');
      if (itemsSource && itemsTarget) itemsTarget.innerHTML = itemsSource.innerHTML;
      itemsEl.closest('cart-items') &&
        itemsEl.closest('cart-items').classList.toggle('is-empty', state.item_count === 0);
    }

    var footerEl = document.getElementById('main-cart-footer');
    if (footerEl && footerEl.dataset.id && state.sections[footerEl.dataset.id]) {
      var footerDoc = new DOMParser().parseFromString(state.sections[footerEl.dataset.id], 'text/html');
      var footerSource = footerDoc.getElementById('main-cart-footer');
      if (footerSource) footerEl.innerHTML = footerSource.innerHTML;
      footerEl.classList.toggle('is-empty', state.item_count === 0);
    }

    var bubbleEl = document.getElementById('cart-icon-bubble');
    if (bubbleEl && state.sections['cart-icon-bubble']) {
      var bubbleDoc = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html');
      var bubbleSource = bubbleDoc.querySelector('.shopify-section');
      if (bubbleSource) bubbleEl.innerHTML = bubbleSource.innerHTML;
    }

    var liveTextEl = document.getElementById('cart-live-region-text');
    if (liveTextEl && state.sections['cart-live-region-text']) {
      var liveDoc = new DOMParser().parseFromString(state.sections['cart-live-region-text'], 'text/html');
      var liveSource = liveDoc.querySelector('.shopify-section');
      if (liveSource) liveTextEl.innerHTML = liveSource.innerHTML;
    }
  }

  function errorsElementFor(mode) {
    return mode === 'drawer'
      ? document.getElementById('CartDrawer-CartErrors')
      : document.getElementById('cart-errors');
  }

  // Last-resort recovery, shared by both modes: a no-op /cart/update.js
  // call (empty `updates`) still returns the true current cart plus
  // whichever `sections` were requested, so the UI can always be brought
  // back in sync with the real cart after a failed step, without ever
  // guessing at state.
  function recoverFromServer(button, message, mode) {
    var sectionIds = getSectionsToRequest(mode);
    return fetch(
      window.routes.cart_update_url,
      Object.assign({}, fetchConfig(), {
        body: JSON.stringify({ updates: {}, sections: sectionIds, sections_url: window.location.pathname }),
      })
    )
      .then(function (response) {
        return response.json();
      })
      .then(function (state) {
        applySections(mode, state);
        var errors = errorsElementFor(mode);
        if (errors) errors.textContent = message;
      })
      .catch(function (e) {
        console.error(e);
        setBusy(button, false);
      });
  }

  // Bundle Recognition can replace 1-4 real cart lines in a single
  // upgrade (e.g. Fresh Morning + Peach Oolong + Lavender Eucalyptus ->
  // Complete Ritual). The snippet renders each present source line as a
  // numbered pair of data attributes (data-source-key-1/
  // data-source-new-qty-1 .. -4) with the new quantity already computed
  // server-side (current quantity minus 1 -- never a full removal unless
  // that line's quantity was already 1), so this only ever has to read
  // them, never recompute anything from possibly-stale DOM state.
  function getSourceLineUpdates(button) {
    var updates = {};
    for (var i = 1; i <= 4; i++) {
      // A hyphen immediately followed by a digit is NOT converted to
      // camelCase by the browser's HTMLElement.dataset -- only a hyphen
      // followed by a lowercase letter is (per the HTML spec's custom
      // data attribute algorithm). data-source-key-1 therefore surfaces
      // as dataset['sourceKey-1'], not dataset.sourceKey1.
      var key = button.dataset['sourceKey-' + i];
      var newQty = button.dataset['sourceNewQty-' + i];
      if (key) updates[key] = parseInt(newQty, 10);
    }
    return updates;
  }

  function upgrade(button) {
    if (button.disabled) return;

    var giftVariantId = button.dataset.giftVariantId;
    var sourceUpdates = getSourceLineUpdates(button);
    if (!giftVariantId || Object.keys(sourceUpdates).length === 0) return;

    var mode = getMode(button);
    setBusy(button, true);

    var sectionIds = getSectionsToRequest(mode);
    var sectionsUrl = window.location.pathname;

    // Add the gift set FIRST, decrement the source line(s) SECOND: if the
    // add fails, nothing has changed yet (safe no-op); the one state that
    // must never happen -- source items gone with no gift set added -- is
    // structurally impossible with this ordering. The decrement step
    // batches every source line into one /cart/update.js call (keyed by
    // line item key, same mechanism assets/soluscent-shipping-protection.
    // js already uses for its own Delivery Protection quantity fixes)
    // instead of one request per line, so a 4-candle Complete Ritual
    // upgrade costs the same two requests as a single-candle one.
    fetch(
      window.routes.cart_add_url,
      Object.assign({}, fetchConfig(), {
        body: JSON.stringify({
          items: [{ id: Number(giftVariantId), quantity: 1 }],
          sections: sectionIds,
          sections_url: sectionsUrl,
        }),
      })
    )
      .then(function (response) {
        return response.json();
      })
      .then(function (addResponse) {
        if (addResponse.status) {
          throw new Error(addResponse.description || 'Could not add the gift set.');
        }

        return fetch(
          window.routes.cart_update_url,
          Object.assign({}, fetchConfig(), {
            body: JSON.stringify({
              updates: sourceUpdates,
              sections: sectionIds,
              sections_url: sectionsUrl,
            }),
          })
        );
      })
      .then(function (response) {
        return response.json();
      })
      .then(function (finalState) {
        if (finalState.errors) {
          // The gift set was added but the source line(s) couldn't be
          // updated. Do not throw away that state or guess -- reload the
          // real cart from the server so the UI always matches it
          // exactly, and surface a message instead of pretending success.
          // This deliberately does not attempt to auto-remove the gift
          // set that was just added: matching this module's existing
          // recovery philosophy (see recoverFromServer() below), never
          // guessing or issuing a second speculative mutation on top of
          // a failure -- the customer keeps everything they had a moment
          // ago plus the new gift set, and can resolve it themselves from
          // an always-accurate cart.
          return recoverFromServer(
            button,
            'Your gift set was added, but we could not update your original items. Please check your cart.',
            mode
          );
        }

        applySections(mode, finalState);
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: mode === 'drawer' ? 'product-form' : 'soluscent-gift-upgrade-page',
          cartData: finalState,
        });
      })
      .catch(function (e) {
        console.error(e);
        recoverFromServer(button, 'Could not complete the upgrade. Please try again.', mode);
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-soluscent-cart-gift-upgrade-submit]');
    if (button) upgrade(button);
  });
})();
